import { useApolloClient } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import { UTILS_ADD_FORM_DATA } from 'src/app/constants/dispatcherId';
import { EDIT_PAGE_CONNECTION_SAVE_DRAFT_ERROR } from 'src/app/constants/errorMessages';
import {
  FEATURE_BRANDING,
  FEATURE_KEY_SELLING_POINTS,
  FEATURE_LINKOUT,
  useFeatureToggleData,
} from 'src/app/featureToggle';
// @ts-ignore
import utils from 'src/app/state/dispatcher/utils';
import { actionUpdateWithDraftId } from 'src/app/state/modules/app/withDraftId';
import { actionUpdateDraftId } from 'src/app/state/modules/session/draftId';
import { actionUpdateDraftStatus } from 'src/app/state/modules/session/draftStatus';
import { actionUpdateDraftId as actionUpdateTealiumDraftId } from 'src/app/state/modules/tealium/draftId';
import { store } from 'src/app/state/store';
import { log } from 'src/app/utils/logging';
import {
  DraftJobDocument,
  DraftJobFlowType,
  DraftStatus,
  type UpdateDraftJobPayload,
  useCreateDraftJobMutation,
  useUpdateDraftJobMutation,
} from 'src/graphql/generated';
import type AppState from 'src/types/appState';
import type { CountryCode } from 'src/types/taxonomy';

import { useCanUpdateExpiryDate } from '../useCanUpdateExpiryDate';
import { useQueryParams } from '../useQueryParams';

import {
  type MapToDraftJobInputOverrides,
  mapGetDraftResponseToDraftPayload,
  mapToDraftJobInput,
} from './mapper';

export const useDraft = () => {
  const {
    advertiserId,
    advertiserNation,
    buyUpSell,
    draftId,
    draftStatus,
    expiryDate,
    flowType,
    queryParamsDraftId,
  } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.advertiserId,
      advertiserNation: state.session.hirerAccount.nation,
      buyUpSell: state.session.buyUpSell,
      draftId: state.session.draftId,
      draftStatus: state.session.draftStatus,
      expiryDate: state.formData.expiryDate,
      flowType: state.formData.flowType,
      queryParamsDraftId: state.app.queryParams.draftid,
    }),
    shallowEqual,
  );

  const client = useApolloClient();
  const { updateParam } = useQueryParams();

  const dispatch = useDispatch();
  const canUpdateExpiryDate = useCanUpdateExpiryDate();

  const hasLinkOutEnabled = useFeatureToggleData(FEATURE_LINKOUT);
  const isBrandingEnabled = useFeatureToggleData(FEATURE_BRANDING);
  const isKeySellingPointsEnabled = useFeatureToggleData(
    FEATURE_KEY_SELLING_POINTS,
  );

  const features = useMemo(
    () => ({
      isBrandingEnabled,
      isKeySellingPointsEnabled,
    }),
    [isBrandingEnabled, isKeySellingPointsEnabled],
  );

  const [createDraftJob] = useCreateDraftJobMutation();
  const [updateDraftJob] = useUpdateDraftJobMutation();

  const createDraft = useCallback(
    async (overrides?: MapToDraftJobInputOverrides) => {
      try {
        const { formData, session }: AppState = store.getState(); // get latest formData from store
        const mappedData = mapToDraftJobInput(
          formData,
          session,
          features,
          overrides,
        );
        const status = DraftStatus.OPEN;

        const { data } = await createDraftJob({
          variables: {
            input: {
              ...mappedData,
              status,
            },
          },
        });

        if (data?.createDraftJob.id) {
          dispatch(actionUpdateDraftId(data.createDraftJob.id));
          dispatch(actionUpdateTealiumDraftId(data.createDraftJob.id));
          dispatch(actionUpdateDraftStatus(status));
          return data.createDraftJob.id;
        }
      } catch (error) {
        log.error('useDraft | createDraft', {
          error,
          advertiserId,
          buyUpSell,
          draftId,
          flowType,
          hasLinkOutEnabled,
          queryParamsDraftId,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createDraftJob, dispatch, isBrandingEnabled, isKeySellingPointsEnabled],
  );

  const updateDraft = useCallback(
    async (isComplete?: boolean, overrides?: MapToDraftJobInputOverrides) => {
      let status = (draftStatus as DraftStatus) || DraftStatus.OPEN;

      if (isComplete === true) {
        status = DraftStatus.CLOSED;
      }

      try {
        const { formData, session }: AppState = store.getState();
        const mappedData = mapToDraftJobInput(
          formData,
          session,
          features,
          overrides,
        );

        const input = {
          ...mappedData,
          id: session.draftId!.toString(),
          flowType:
            flowType === 'edit'
              ? DraftJobFlowType.EDIT
              : DraftJobFlowType.CREATE,
          status,
        };

        const { data } = await updateDraftJob({
          variables: {
            input,
          },
        });

        if (data?.updateDraftJob.__typename === 'UpdateDraftJobPayload') {
          const payload = data.updateDraftJob as UpdateDraftJobPayload;
          dispatch(actionUpdateDraftId(payload.draftId));
          dispatch(actionUpdateTealiumDraftId(payload.draftId.toString()));
          dispatch(actionUpdateDraftStatus(status));
        }

        if (
          data?.updateDraftJob.__typename === 'UpdateDraftJobExpressPayload'
        ) {
          const draftInteger = data.updateDraftJob.draftInteger;
          dispatch(actionUpdateDraftId(draftInteger.toString()));
          dispatch(actionUpdateTealiumDraftId(draftInteger.toString()));
          dispatch(actionUpdateDraftStatus(status));
        }
      } catch (error) {
        log.error('useDraft | updateDraft', {
          error,
          advertiserId,
          buyUpSell,
          draftId,
          draftStatus: status,
          hasLinkOutEnabled,
          queryParamsDraftId,
        });
        throw error;
      }
    },
    [
      draftStatus,
      features,
      flowType,
      updateDraftJob,
      dispatch,
      advertiserId,
      buyUpSell,
      draftId,
      hasLinkOutEnabled,
      queryParamsDraftId,
    ],
  );

  const getDraft = useCallback(async () => {
    if (queryParamsDraftId) {
      try {
        // using client here directly is because
        // lazy query from apollo 3.4.17 doesn't return promise
        // so can't render-blocking UI (e.g. job title field will be empty)
        // since 3.5.0+, lazy query returns promise
        // however errorPolicy: 'all' of 3.5.0+ will have different behaviour on onCompleted & onError
        // TODO revisit upgrading to latest apollo client
        const { data } = await client.query({
          query: DraftJobDocument,
          variables: { advertiserId, id: queryParamsDraftId },
        });

        if (data?.draftJob?.__typename === 'DraftJobSuccessPayload') {
          const mappedDraftData = mapGetDraftResponseToDraftPayload(
            data.draftJob,
          )({
            countryCode: advertiserNation.toLowerCase() as CountryCode,
            hasLinkOutEnabled: Boolean(hasLinkOutEnabled),
          });

          dispatch(utils(UTILS_ADD_FORM_DATA, mappedDraftData));
          dispatch(actionUpdateDraftStatus(data.draftJob?.status));

          // Note: We update the draft id because even though we might have
          // requested an adcentre Draft id we can have a new Draft API id
          // return in they payload as we are auto migrating drafts on request
          const newDraftId = data?.draftJob?.draft?.id;
          if (newDraftId) {
            dispatch(actionUpdateDraftId(newDraftId));
            dispatch(actionUpdateTealiumDraftId(newDraftId));
            updateParam('draftid', newDraftId);
          }
        }
      } catch (error) {
        log.error('useDraft | getDraft', {
          error,
          advertiserId,
          buyUpSell,
          draftId,
          flowType,
          hasLinkOutEnabled,
          queryParamsDraftId,
        });

        dispatch(actionUpdateWithDraftId(true));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParamsDraftId, client, advertiserId, hasLinkOutEnabled, dispatch]);

  const saveDraft = useCallback(
    async (
      isComplete: boolean = false,
      rejectOnError: boolean = false,
      overrides?: MapToDraftJobInputOverrides,
    ) => {
      // update existing or complete draft

      if (draftId || isComplete) {
        return new Promise<void>((resolve, reject) => {
          if (expiryDate && !canUpdateExpiryDate && flowType !== 'edit') {
            log.error(
              'useDraft | saveDraft: Has expiryDate for non agency and non gov account',
              {
                advertiserId,
                draftId,
                expiryDate,
              },
            );
          }

          updateDraft(isComplete, overrides)
            .then(resolve)
            .catch((error) => {
              log.error('useDraft | saveDraft', {
                error,
                advertiserId,
                draftId,
                flowType,
                isComplete,
              });

              // Block create flow from proceeding
              // i.e. critical error like failed payment
              if (
                (flowType === 'create' && isComplete) ||
                (flowType === 'create' && rejectOnError)
              ) {
                return reject(error);
              }

              // Handle error for failed draft save in Edit flow
              if (flowType === 'edit' && !isComplete) {
                return reject(new Error(EDIT_PAGE_CONNECTION_SAVE_DRAFT_ERROR));
              }

              resolve();
            });
        });
      }

      return createDraft();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [advertiserId, canUpdateExpiryDate, draftId, expiryDate, flowType],
  );

  return {
    createDraft,
    updateDraft,
    getDraft,
    saveDraft,
  };
};
